import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { ScoredGeography } from '@/types/insights';
import { round } from '@/utils/numbers';

const TITLE = 'Texas Market Trends -- Summary Report';

function addHeader(doc: jsPDF, subtitle: string) {
  doc.setFontSize(16);
  doc.text(TITLE, 14, 18);
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(subtitle, 14, 25);
  doc.text(`Generated ${new Date().toLocaleString()}`, 14, 30);
  doc.setTextColor(0);
}

/** Exports a table-style summary of every scored geography currently in view. */
export function exportSummaryPdf(geographies: ScoredGeography[], filename = 'texas-market-map-summary.pdf') {
  const doc = new jsPDF();
  addHeader(doc, `${geographies.length} counties covered`);

  autoTable(doc, {
    startY: 36,
    head: [['County', 'Temperature', 'Sell-Speed', 'Opportunity', 'Cooling Risk', 'Confidence']],
    body: geographies.map(({ record, insights }) => [
      record.geoName,
      `${insights.hotColdScore.label} (${round(insights.hotColdScore.value, 0)})`,
      `${insights.sellSpeedShift.label} (${round(insights.sellSpeedShift.value, 0)})`,
      `${round(insights.opportunityScore.value, 0)}/100`,
      `${round(insights.coolingRisk.value, 0)}/100`,
      `${round(insights.confidenceScore.value, 0)}/100`,
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [30, 41, 59] },
  });

  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(
    'Scores are derived by MAY Assistant from public source data (see in-app source list) and are not official published figures.',
    14,
    doc.internal.pageSize.getHeight() - 10,
  );

  doc.save(filename);
}

/** Exports a single geography's detail-drawer content as a one-page PDF. */
export function exportGeoDetailPdf(scored: ScoredGeography, filename?: string) {
  const { record, insights } = scored;
  const doc = new jsPDF();
  addHeader(doc, `${record.geoName} County -- ${record.periodLabel}`);

  autoTable(doc, {
    startY: 36,
    head: [['Metric', 'Value']],
    body: [
      ['Hot / Cold', `${insights.hotColdScore.label} (${round(insights.hotColdScore.value, 0)})`],
      ['Sell-Speed Shift', `${insights.sellSpeedShift.label} (${round(insights.sellSpeedShift.value, 0)})`],
      ['Inventory Pressure', `${round(insights.inventoryPressure.value, 0)}/100`],
      ['Opportunity Score', `${round(insights.opportunityScore.value, 0)}/100`],
      ['Cooling Risk', `${round(insights.coolingRisk.value, 0)}/100`],
      ['Buyer Competition', `${round(insights.buyerCompetition.value, 0)}/100`],
      ['Confidence', `${round(insights.confidenceScore.value, 0)}/100`],
      ['Median Sale Price', record.resolved.medianSalePrice ? `$${record.resolved.medianSalePrice.toLocaleString()}` : '--'],
      ['Median Days on Market', record.resolved.medianDaysOnMarket ?? '--'],
      ['Months of Inventory', record.resolved.monthsOfInventory ?? '--'],
      ['Contributing Sources', insights.contributingSources.join(', ') || '--'],
    ],
    styles: { fontSize: 9 },
    headStyles: { fillColor: [30, 41, 59] },
  });

  const afterTableY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
  doc.setFontSize(10);
  doc.text('Explanation', 14, afterTableY);
  doc.setFontSize(9);
  const wrapped = doc.splitTextToSize(insights.explanations.join('  '), 180);
  doc.text(wrapped, 14, afterTableY + 6);

  doc.save(filename ?? `texas-market-map-${record.geoId}.pdf`);
}
